import { useEffect, useRef, useState } from "react";

function read<T>(key: string, initial: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored === null ? initial : (JSON.parse(stored) as T);
  } catch {
    // Unreadable or corrupt (hand-edited, or written by an older shape) —
    // the default is always a safe answer here.
    return initial;
  }
}

function write<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota exceeded or storage blocked. The selection still works for this
    // session; it just will not be there next time.
  }
}

/**
 * useState that survives a reload, backed by localStorage.
 *
 * For form selections a person re-picks every visit — the syllabus a whole
 * team works through for weeks at a time — not for anything server-owned.
 * The stored value is a hint, never a source of truth: it is echoed back into
 * a <select> whose options come from the API, so a value that no longer
 * exists there simply shows as "nothing selected".
 *
 * `key` may change across the life of the component — e.g. scoped by a
 * collection id so a component that stays mounted across a collection switch
 * (only a route param changed) doesn't carry one collection's stored value
 * into another's. useState's lazy initializer only runs once per component
 * instance, so a bare key change would neither re-read the new key's stored
 * value nor stop writing into it under the old one.
 *
 * Handled inside one effect rather than split across two: a key change and a
 * value change both re-run this same effect, and the branch below is what
 * tells them apart — a split into a "detect key change" effect plus a
 * "write" effect fires both in the same commit, before the reset from the
 * first has actually reached the second's closed-over `value`, so the write
 * effect still stamps the new key with the old value on that transition
 * render. Tracking the key that produced the current `value` avoids that: the
 * mismatch only exists for exactly the one render prompted by the key change.
 */
export function useStickyState<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => read(key, initial));
  // The key `value` was last read for or written under — not necessarily the
  // latest prop, so the effect can tell "key changed under me" apart from
  // "value changed, go persist it".
  const valueKeyRef = useRef(key);

  useEffect(() => {
    if (valueKeyRef.current !== key) {
      // The identity this hook is keyed to changed. Re-read the new key's own
      // stored value rather than persisting the old identity's value under it.
      valueKeyRef.current = key;
      setValue(read(key, initial));
      return;
    }
    write(key, value);
    // `initial` is intentionally not a dependency: it is a per-call constant
    // (often a literal at the call site), and including it would re-run this
    // on every render if the caller passes a fresh object/array each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value]);

  return [value, setValue] as const;
}
